import React, { useState, useEffect, useCallback } from 'react';
import { Award, Edit2, Check, X, Star, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { formatApiError } from '../../utils/apiError';
import { createTextPicker } from '../../utils/localizedContent';


const LEVEL_BADGE = {
  bronze:   'bg-amber-700/30 text-amber-400',
  silver:   'bg-gray-400/20 text-gray-300',
  gold:     'bg-[#C9A962]/20 text-[#C9A962]',
  platinum: 'bg-cyan-400/20 text-cyan-300',
};

export default function AdminLoyalty({ lang, setMessage }) {
  const [loyaltyUsers, setLoyaltyUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ points: 0, reason: '' });
  const [saving, setSaving] = useState(false);

  const l = createTextPicker(lang);

  const fetchLoyaltyUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/loyalty/users`);
      setLoyaltyUsers(data.users || []);
    } catch (err) {
      console.error('Failed to fetch loyalty users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLoyaltyUsers(); }, [fetchLoyaltyUsers]);

  const openEdit = (user) => {
    setEditingUser(user.user_id);
    setEditForm({ points: 0, reason: '' });
  };

  const handleSavePoints = async (userId) => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/loyalty/adjust`, {
        userId,
        points: parseInt(editForm.points) || 0,
        reason: editForm.reason
      });
      setMessage(l('Puntos ajustados', 'Points adjusted'));
      setEditingUser(null);
      fetchLoyaltyUsers();
    } catch (err) {
      setMessage(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const getLevelLabel = (pts) => {
    if (pts >= 10000) return { label: 'Platinum', key: 'platinum' };
    if (pts >= 2500) return { label: 'Gold', key: 'gold' };
    if (pts >= 500) return { label: 'Silver', key: 'silver' };
    return { label: 'Bronze', key: 'bronze' };
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-white font-bold text-xl flex items-center gap-2">
          <Award size={20} className="text-[#C9A962]" />
          {l('Programa de Fidelidad', 'Loyalty Program')}
        </h2>
        <div className="text-[#A1A1AA] text-sm">
          {loyaltyUsers.length} {l('usuarios', 'users')}
        </div>
      </div>

      {/* Level info banner */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[['Bronze', '0–1', 'amber-700'], ['Silver', '500+', 'gray-400'], ['Gold', '2500+', 'C9A962'], ['Platinum', '10000+', 'cyan-400']].map(([name, req]) => (
          <div key={name} className="bg-[#12121A] border border-white/5 rounded-xl p-3 text-center">
            <p className="text-white text-xs font-semibold">{name}</p>
            <p className="text-[#52525B] text-xs">{req} pts</p>
          </div>
        ))}
      </div>

      {/* Users table */}
      {loading ? (
        <div className="text-center py-12 text-[#A1A1AA] text-sm">{l('Cargando...', 'Loading...')}</div>
      ) : (
        <div className="space-y-2">
          {loyaltyUsers.map((u, idx) => {
            const level = getLevelLabel(u.lifetime_points || 0);
            const isEditing = editingUser === u.user_id;
            return (
              <div
                key={u.user_id}
                className="bg-[#12121A] border border-white/5 rounded-xl p-4"
              >
                <div className="flex items-center gap-4">
                  {/* Rank */}
                  <span className="text-[#52525B] text-xs font-bold w-6 text-center">#{idx + 1}</span>

                  {/* Avatar */}
                  <div className="w-9 h-9 rounded-full bg-[#C9A962]/20 flex items-center justify-center text-[#C9A962] font-bold text-sm flex-shrink-0">
                    {(u.name || u.email || '?')[0].toUpperCase()}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{u.name || 'Sin nombre'}</p>
                    <p className="text-[#52525B] text-xs truncate">{u.email}</p>
                  </div>

                  {/* Points */}
                  <div className="text-right flex-shrink-0">
                    <p className="text-[#C9A962] font-bold text-sm">{(u.current_points || 0).toLocaleString()} pts</p>
                    <p className="text-[#52525B] text-xs">{(u.lifetime_points || 0).toLocaleString()} total</p>
                  </div>

                  {/* Level badge */}
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${LEVEL_BADGE[level.key]}`}>
                    {level.label}
                  </span>

                  {/* Edit button */}
                  <button
                    onClick={() => isEditing ? setEditingUser(null) : openEdit(u)}
                    className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
                      isEditing ? 'bg-[#C9A962]/20 text-[#C9A962]' : 'bg-white/5 text-[#A1A1AA] hover:text-white'
                    }`}
                    title={l('Editar puntos', 'Edit points')}
                  >
                    <Edit2 size={13} />
                  </button>
                </div>

                {/* Inline edit form */}
                {isEditing && (
                  <div className="mt-4 pt-4 border-t border-white/5">
                    <p className="text-[#A1A1AA] text-xs mb-3">
                      {l('Ajustar puntos de', 'Adjust points for')} <span className="text-white font-medium">{u.name || u.email}</span>
                      {' — '}{l('actual:', 'current:')} <span className="text-[#C9A962]">{(u.current_points || 0).toLocaleString()} pts</span>
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <div>
                        <label className="text-[#52525B] text-xs block mb-1">{l('Puntos (+/-)', 'Points (+/-)')}</label>
                        <input
                          type="number"
                          value={editForm.points}
                          onChange={e => setEditForm(f => ({ ...f, points: e.target.value }))}
                          placeholder="+100 o -50"
                          className="bg-[#1C1C28] border border-white/10 text-white rounded-lg px-3 py-2 text-sm w-32 focus:outline-none focus:border-[#C9A962]"
                        />
                      </div>
                      <div className="flex-1 min-w-[160px]">
                        <label className="text-[#52525B] text-xs block mb-1">{l('Motivo (opcional)', 'Reason (optional)')}</label>
                        <input
                          type="text"
                          value={editForm.reason}
                          onChange={e => setEditForm(f => ({ ...f, reason: e.target.value }))}
                          placeholder={l('Ej: Premio torneo', 'e.g., Tournament prize')}
                          className="w-full bg-[#1C1C28] border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A962]"
                        />
                      </div>
                      <div className="flex gap-2 self-end">
                        <button
                          onClick={() => handleSavePoints(u.user_id)}
                          disabled={saving}
                          className="flex items-center gap-1.5 px-4 py-2 bg-[#C9A962] text-[#0A1612] rounded-lg text-sm font-semibold hover:bg-[#B8984E] transition-colors disabled:opacity-60"
                        >
                          <Check size={14} />{saving ? '...' : l('Aplicar', 'Apply')}
                        </button>
                        <button
                          onClick={() => setEditingUser(null)}
                          className="flex items-center gap-1.5 px-4 py-2 bg-white/5 text-[#A1A1AA] rounded-lg text-sm font-semibold hover:bg-white/10 transition-colors"
                        >
                          <X size={14} />{l('Cancelar', 'Cancel')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {loyaltyUsers.length === 0 && (
            <div className="text-center py-12 text-[#52525B]">
              <TrendingUp size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">{l('No hay datos de fidelidad', 'No loyalty data yet')}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
